import { styled } from "baseui";
import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Flex } from "../components/Flex";
import { HeadingMedium, LabelSmall } from "baseui/typography";
import { Button, KIND, SIZE } from "baseui/button";
import { Download, Filter, Calendar, Home } from "lucide-react";
import { Skeleton } from "baseui/skeleton";
import { Drawer } from "baseui/drawer";
import { Popover } from "baseui/popover";

const SkeletonComponent = Skeleton as any;
import { useDashboardData, dashboardReadsCombined, DashboardFilter, DashboardFilterType } from "../hooks/useDashboardData";
import { useUserPermissions } from "../hooks/useUserPermissions";
import { useIsPhone } from "../hooks/useIsPhone";
import { useDashboardMetrics } from "../hooks/useDashboardMetrics";
import { getDateRanges } from "../helpers/dateUtils";
import { DashboardFilters } from "../components/dashboard/DashboardFilters";
import { BaseDatePicker } from "../components/BaseDatePicker";
import { KPICards } from "../components/dashboard/KPICards";
import { ImageAnalyzerStatus } from "../components/dashboard/ImageAnalyzerStatus";
import { AccessTypeChart } from "../components/dashboard/charts/AccessTypeChart";
import { StatusDistributionChart } from "../components/dashboard/charts/StatusDistributionChart";
import { TrendChart } from "../components/dashboard/charts/TrendChart";
import { InOutChart } from "../components/dashboard/charts/InOutChart";
import { TenantDashboard } from "../components/dashboard/tenant/TenantDashboard";

// Styled components
const DashboardContainer = styled('div', {
  backgroundColor: '#f9fafb',
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '0px 30px 60px 30px',
  '@media (max-width: 768px)': {
    padding: '0px 16px 60px 16px',
  },
});

const LiveBadge = styled('div', {
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: '#fff',
  padding: '6px 12px',
  borderRadius: '16px',
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
  fontSize: '12px',
  fontWeight: 500,
});

// SAM-216: base dot registers the pulse keyframes and the connected look
// (tenant primary + pulse). The disconnected look (static red) is applied at
// render time via an inline style override, keeping this compatible with
// BaseUI's static `styled` typing.
const LiveDot = styled('div', {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: 'var(--tenant-primary, #2563eb)',
  marginRight: '6px',
  animation: 'pulse 2s infinite',
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.5 },
  },
});

const QuickFiltersContainer = styled('div', {
  display: 'flex',
  gap: '8px',
  marginBottom: '24px',
  flexWrap: 'wrap',
});

const ChartsGrid = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 400px), 1fr))',
  gap: '20px',
  marginBottom: '24px',
  '@media (max-width: 768px)': {
    gridTemplateColumns: '1fr',
  },
});

const FullWidthSection = styled('div', {
  marginBottom: '24px',
});

const MobileFilterButton = styled('div', {
  display: 'none',
  '@media (max-width: 768px)': {
    display: 'block',
  },
});

const DesktopFilters = styled('div', {
  display: 'block',
  '@media (max-width: 768px)': {
    display: 'none',
  },
});

const StickyHeaderContainer = styled('div', {
  position: 'sticky',
  top: 0,
  backgroundColor: '#f9fafb',
  zIndex: 100,
  paddingTop: '16px',
  paddingBottom: '20px',
  marginLeft: '-30px',
  marginRight: '-30px',
  paddingLeft: '30px',
  paddingRight: '30px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
  '@media (max-width: 768px)': {
    marginLeft: '-16px',
    marginRight: '-16px',
    paddingLeft: '16px',
    paddingRight: '16px',
  },
});

const PropertyBanner = styled('div', {
  backgroundColor: '#F0F9FF',
  border: '1px solid #BAE6FD',
  borderRadius: '8px',
  padding: '12px 16px',
  marginBottom: '16px',
  fontSize: '13px',
  color: '#0369A1',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

const OverviewDashboard = () => {
  const { isPropertyManager, isAdmin, isXhinolaAdmin, mainEstate, isEstateGroupAdmin, isEstateGroupFrontDesk, isTenantFrontDesk, isLoading: permissionsLoading } = useUserPermissions();
  const isPhone = useIsPhone();
  const dateRanges = useMemo(() => getDateRanges(), []);
  
  // Estados para filtros - Default to 'today'
  const [timeRange, setTimeRange] = useState<DashboardFilterType>('today');
  const [filters, setFilters] = useState<DashboardFilter>({
    timeRange: 'today',
  });
  const [appliedFilters, setAppliedFilters] = useState<DashboardFilter>(filters);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);

  // Construir filtros con fechas
  const filtersWithDates = useMemo(() => {
    const result: DashboardFilter = { ...appliedFilters };

    // Property manager: filter by their estate
    if (isPropertyManager && !isAdmin && mainEstate) {
      result.estate_id = mainEstate;
    }

    // If custom date range is set, use those dates directly
    if (appliedFilters.timeRange === 'custom' && appliedFilters.startDate && appliedFilters.endDate) {
      return result;
    }

    // Otherwise, use predefined time ranges
    if (appliedFilters.timeRange && appliedFilters.timeRange !== 'custom') {
      // SAM-217: set BOTH startDate and endDate on every preset. Previously
      // week/month/ytd only set startDate, so an endDate left over from a prior
      // custom range stayed pinned and silently clipped the results.
      if (appliedFilters.timeRange === 'today') {
        result.startDate = dateRanges.today.start;
        result.endDate = dateRanges.today.end;
      } else if (appliedFilters.timeRange === 'week') {
        result.startDate = dateRanges.lastWeek.start;
        result.endDate = dateRanges.lastWeek.end;
      } else if (appliedFilters.timeRange === 'month') {
        result.startDate = dateRanges.thisMonth.start;
        result.endDate = dateRanges.thisMonth.end;
      } else if (appliedFilters.timeRange === 'ytd') {
        result.startDate = dateRanges.ytd.start;
        result.endDate = dateRanges.ytd.end;
      }
    }
    
    return result;
  }, [appliedFilters, dateRanges, isPropertyManager, isAdmin, mainEstate]);

  // Consultar datos
  const { data: dashboardData, isLoading, isError } = useDashboardData(filtersWithDates, { combined: dashboardReadsCombined({ isAdmin, isXhinolaAdmin, isEstateGroupAdmin, isEstateGroupFrontDesk, isTenantFrontDesk }), enabled: !permissionsLoading });

  // SAM-216: real connection state for the "Live / Offline" indicator.
  // Connected = the browser is online AND the last Dashboard query did not fail.
  const [isOnline, setIsOnline] = useState<boolean>(typeof navigator !== 'undefined' ? navigator.onLine : true);
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);
  const isConnected = isOnline && !isError;

  // SAM-217: human-readable label of the range actually being queried, so the
  // operator can verify at a glance what the KPIs and charts reflect.
  const effectiveRangeLabel = useMemo(() => {
    const fmt = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : undefined);
    const start = fmt(filtersWithDates.startDate);
    const end = fmt(filtersWithDates.endDate);
    if (start && end) return start === end ? start : `${start} – ${end}`;
    if (start) return `From ${start}`;
    return 'All time';
  }, [filtersWithDates.startDate, filtersWithDates.endDate]);

  // Determinar granularidad para gráficos
  const granularity = useMemo(() => {
    if (appliedFilters.timeRange === 'today') return 'hour';
    if (appliedFilters.timeRange === 'week') return 'day';
    if (appliedFilters.timeRange === 'month') return 'day';
    if (appliedFilters.timeRange === 'ytd') return 'month';
    return 'day';
  }, [appliedFilters.timeRange]);

  // Calcular métricas
  const { metrics, comparison } = useDashboardMetrics(
    dashboardData?.data || [],
    undefined,
    granularity
  );

  // Handlers
  const handleQuickFilterChange = (range: DashboardFilterType) => {
    setTimeRange(range);
    // Clear custom date fields when switching to predefined ranges
    const newFilters: DashboardFilter = { timeRange: range };
    setFilters(newFilters);
    setAppliedFilters(newFilters);
  };

  const handleFiltersChange = (newFilters: DashboardFilter) => {
    setFilters(newFilters);
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setIsFilterDrawerOpen(false);
  };

  const handleClearFilters = () => {
    const clearedFilters: DashboardFilter = { timeRange: 'today' };
    setFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
    setTimeRange('today');
  };

  const handleExport = () => {
    // TODO: Implementar exportación CSV
  };

  // Preparar datos para KPIs
  const kpiData = [
    {
      label: 'Total Access',
      value: metrics.totalAccess,
      change: comparison?.totalChange,
      format: 'number' as const,
    },
    {
      label: 'Pending',
      value: metrics.pendingAccess,
      change: comparison?.pendingChange,
      format: 'number' as const,
    },
    {
      label: 'Currently In',
      value: metrics.inAccess,
      change: comparison?.inChange,
      format: 'number' as const,
    },
    {
      label: 'Checked Out',
      value: metrics.outAccess,
      format: 'number' as const,
    },
    {
      label: 'Conversion Rate',
      value: metrics.conversionRate,
      change: comparison?.conversionRateChange,
      format: 'percentage' as const,
    },
    {
      label: 'Avg Guests',
      value: metrics.averageGuests,
      format: 'decimal' as const,
    },
  ];

  return (
    <DashboardContainer>
      {/* Header. SAM-1309 (U10): on a phone it is two rows (title and Live; then the actions), so the title does not break
          and nothing leaves the screen. The Image Analyzer status is for the Xhinola team only. */}
      {(() => {
        const title = <HeadingMedium margin={0}>{isPropertyManager && !isAdmin ? 'My Property Dashboard' : 'Analytics Dashboard'}</HeadingMedium>;
        const live = (
          <LiveBadge>
            <LiveDot style={isConnected ? undefined : { backgroundColor: 'var(--danger)', animation: 'none' }} />
            {isConnected ? 'Live' : 'Offline'}
          </LiveBadge>
        );
        const filtersButton = (
          <MobileFilterButton>
            <Button
              onClick={() => setIsFilterDrawerOpen(true)}
              kind={KIND.secondary}
              size={SIZE.compact}
              startEnhancer={() => <Filter size={16} />}
            >
              Filters
            </Button>
          </MobileFilterButton>
        );
        const exportButton = (
          <Button
            onClick={handleExport}
            kind={KIND.secondary}
            size={SIZE.compact}
            startEnhancer={() => <Download size={16} />}
          >
            Export
          </Button>
        );
        const analyzer = isXhinolaAdmin ? <ImageAnalyzerStatus /> : null;
        if (isPhone) {
          return (
            <div style={{ marginBottom: '16px' }}>
              <Flex justifyContent="space-between" alignItems="center" gap="12px" marginBottom="12px">
                {title}
                {live}
              </Flex>
              <Flex alignItems="center" gap="8px" flexWrap="wrap">
                {analyzer}
                {filtersButton}
                {exportButton}
              </Flex>
            </div>
          );
        }
        return (
          <Flex justifyContent="space-between" alignItems="center" marginBottom="24px" flexWrap="wrap" gap="12px">
            {title}
            <Flex alignItems="center" gap="16px" flexWrap="wrap">
              {analyzer}
              {live}
              {filtersButton}
              {exportButton}
            </Flex>
          </Flex>
        );
      })()}

      {/* Property Manager Banner */}
      {isPropertyManager && !isAdmin && (
        <PropertyBanner>
          <Home size={16} />
          You are viewing data for your assigned properties only
        </PropertyBanner>
      )}

      {/* Quick Time Range Filters */}
      <StickyHeaderContainer>
        <QuickFiltersContainer>
        <Button
          onClick={() => handleQuickFilterChange('today')}
          kind={KIND.secondary}
          size={SIZE.compact}
          overrides={{
            BaseButton: {
              style: {
                backgroundColor: timeRange === 'today' ? 'var(--tenant-primary, #111827)' : '#fff',
                color: timeRange === 'today' ? '#fff' : '#111827',
                ':hover': {
                  backgroundColor: timeRange === 'today' ? 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)' : '#f3f4f6',
                },
              },
            },
          }}
        >
          Today
        </Button>
        <Button
          onClick={() => handleQuickFilterChange('week')}
          kind={KIND.secondary}
          size={SIZE.compact}
          overrides={{
            BaseButton: {
              style: {
                backgroundColor: timeRange === 'week' ? 'var(--tenant-primary, #111827)' : '#fff',
                color: timeRange === 'week' ? '#fff' : '#111827',
                ':hover': {
                  backgroundColor: timeRange === 'week' ? 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)' : '#f3f4f6',
                },
              },
            },
          }}
        >
          Last 7 Days
        </Button>
        <Button
          onClick={() => handleQuickFilterChange('month')}
          kind={KIND.secondary}
          size={SIZE.compact}
          overrides={{
            BaseButton: {
              style: {
                backgroundColor: timeRange === 'month' ? 'var(--tenant-primary, #111827)' : '#fff',
                color: timeRange === 'month' ? '#fff' : '#111827',
                ':hover': {
                  backgroundColor: timeRange === 'month' ? 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)' : '#f3f4f6',
                },
              },
            },
          }}
        >
          This Month
        </Button>
        <Button
          onClick={() => handleQuickFilterChange('ytd')}
          kind={KIND.secondary}
          size={SIZE.compact}
          overrides={{
            BaseButton: {
              style: {
                backgroundColor: timeRange === 'ytd' ? 'var(--tenant-primary, #111827)' : '#fff',
                color: timeRange === 'ytd' ? '#fff' : '#111827',
                ':hover': {
                  backgroundColor: timeRange === 'ytd' ? 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)' : '#f3f4f6',
                },
              },
            },
          }}
        >
          Year to Date
        </Button>
        <Popover
          isOpen={isDateRangeOpen}
          onClickOutside={() => setIsDateRangeOpen(false)}
          content={() => (
            <div style={{ padding: '8px' }}>
              <BaseDatePicker
                inline
                mode="range"
                rangeValue={{
                  from: filters.startDate ? new Date(filters.startDate) : undefined,
                  to: filters.endDate ? new Date(filters.endDate) : undefined,
                }}
                onRangeChange={(range) => {
                  if (range?.from && range?.to) {
                    // Both dates selected - apply filters and close
                    const newFilters = {
                      ...filters,
                      startDate: range.from.toISOString(),
                      endDate: range.to.toISOString(),
                      timeRange: 'custom' as DashboardFilterType,
                    };
                    setFilters(newFilters);
                    setAppliedFilters(newFilters);
                    setTimeRange('custom');
                    setIsDateRangeOpen(false);
                  } else if (range?.from && !range?.to) {
                    // Only start date selected - keep in state, calendar stays open
                    const newFilters = {
                      ...filters,
                      startDate: range.from.toISOString(),
                      endDate: undefined,
                      timeRange: 'custom' as DashboardFilterType,
                    };
                    setFilters(newFilters);
                  } else if (!range || (!range.from && !range.to)) {
                    // Cleared - reset to default (today)
                    const newFilters: DashboardFilter = { timeRange: 'today' };
                    setFilters(newFilters);
                    setAppliedFilters(newFilters);
                    setTimeRange('today');
                    setIsDateRangeOpen(false);
                  }
                }}
                placeholder="Select date range..."
              />
            </div>
          )}
            placement="bottomLeft"
          returnFocus={false}
          autoFocus={false}
            overrides={{
              Body: {
                style: {
                  zIndex: 10000,
                  backgroundColor: '#fff',
                  borderRadius: '16px',
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                },
              },
              Inner: {
                style: {
                  backgroundColor: '#fff',
                  borderRadius: '16px',
                },
              },
            }}
        >
          <div>
            <Button
              onClick={() => setIsDateRangeOpen(!isDateRangeOpen)}
              kind={KIND.secondary}
              size={SIZE.compact}
              startEnhancer={() => <Calendar size={16} />}
              overrides={{
                BaseButton: {
                  style: {
                    backgroundColor: timeRange === 'custom' ? 'var(--tenant-primary, #111827)' : '#fff',
                    color: timeRange === 'custom' ? '#fff' : '#111827',
                    ':hover': {
                      backgroundColor: timeRange === 'custom' ? 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)' : '#f3f4f6',
                    },
                  },
                },
              }}
            >
              Date Range
            </Button>
          </div>
        </Popover>
        {/* SAM-1312 (U33): on a phone the header's Filters opens the drawer; this one only opens the desktop panel. */}
        {!isPhone && (
        <Button
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          kind={KIND.secondary}
          size={SIZE.compact}
          startEnhancer={() => <Filter size={16} />}
          overrides={{
            BaseButton: {
              style: {
                backgroundColor: showAdvancedFilters ? 'var(--tenant-primary, #111827)' : '#fff',
                color: showAdvancedFilters ? '#fff' : '#111827',
                ':hover': {
                  backgroundColor: showAdvancedFilters ? 'color-mix(in srgb, var(--tenant-primary, #111827) 85%, black)' : '#f3f4f6',
                },
              },
            },
          }}
        >
          Filters
        </Button>
        )}
      </QuickFiltersContainer>

        {/* SAM-217: effective range being queried */}
        <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '12px' }}>
          Showing: <span style={{ fontWeight: 600, color: '#374151' }}>{effectiveRangeLabel}</span>
        </div>

        {/* Desktop Advanced Filters */}
        {showAdvancedFilters && (
          <DesktopFilters>
            <DashboardFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onApply={handleApplyFilters}
              onClear={handleClearFilters}
              isExpanded={true}
            />
          </DesktopFilters>
        )}

        {/* KPI Cards */}
        {isLoading ? (
          <SkeletonComponent width="100%" height="120px" animation />
        ) : (
          <KPICards kpis={kpiData} />
        )}
      </StickyHeaderContainer>

      {/* Mobile Filter Drawer */}
      <Drawer
        isOpen={isFilterDrawerOpen}
        autoFocus
        onClose={() => setIsFilterDrawerOpen(false)}
        anchor="left"
      >
        <DashboardFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
        />
      </Drawer>

      {/* Charts Grid - Row 1 */}
      <ChartsGrid>
        {isLoading ? (
          <>
            <SkeletonComponent width="100%" height="400px" animation />
            <SkeletonComponent width="100%" height="400px" animation />
          </>
        ) : (
          <>
            <TrendChart data={metrics.timeSeriesData} />
            <StatusDistributionChart data={metrics.statusDistribution} />
          </>
        )}
      </ChartsGrid>

      {/* Access Type & In/Out Charts */}
      <ChartsGrid>
        {isLoading ? (
          <>
            <SkeletonComponent width="100%" height="400px" animation />
            <SkeletonComponent width="100%" height="400px" animation />
          </>
        ) : (
          <>
            <AccessTypeChart data={metrics.accessTypeDistribution} />
            <InOutChart inCount={metrics.inAccess} outCount={metrics.outAccess} />
          </>
        )}
      </ChartsGrid>


      {/* Empty State */}
      {!isLoading && metrics.totalAccess === 0 && (
        <Flex
          justifyContent="center"
          alignItems="center"
          padding="60px 20px"
        >
          <div style={{ textAlign: 'center' }}>
            <HeadingMedium color="#6b7280">No Data Available</HeadingMedium>
            <LabelSmall color="#9ca3af">
              Try adjusting your filters to see more results
            </LabelSmall>
          </div>
        </Flex>
      )}
    </DashboardContainer>
  );
};

// SAM-942: the D2 Tenant dashboard is built widget by widget; until its mock is fully covered it
// opens with /dashboard?view=tenant and the Overview stays the default.
export const Dashboard = () => {
  const [params] = useSearchParams();
  return params.get('view') === 'tenant' ? <TenantDashboard /> : <OverviewDashboard />;
};
